import {RequestMethod} from '#lit/common/constants/index.js';
import type {DatasetType} from '#lit/types/datasets.js';
import {FlyErrorWarning} from '#lit/utils/errors.js';
import {fetcher} from '#lit/utils/http.js';
import {HTTPService} from '#lit/lib/http_service/index.js';
import type {
  CheckFieldUniquenessResponse,
  CreateDatasetGroupResponse,
  FetchAllDatasetGroupsResponse,
  FieldSearchResponse,
  UpdateDatasetGroupResponse,
} from './types.js';
import {isLeft, left} from '#lit/types/Either.js';
import {renderErrorNotification} from '#lit/utils/notifications.js';
import type {HttpServiceError} from '#lit/lib/http_service/errors.js';
import type {HttpServiceResponse} from '#lit/types/http_service.js';

const httpService = new HTTPService();

const BASE_DATASET_GROUP_URL = (id: number) => `/api/v2/dataset_groups/${id}`;

const BASE_SOURCE_CONNECTION_URL = (id: number) =>
  `/api/v2/source_connections/${id}/`;

/**
 * Fetches fields from an organization's data warehouse.
 */
async function fieldSearch(
  signal: AbortSignal,
  params: {
    datasetGroupId: number;
    datasetId: number;
    datasetType: DatasetType;
    query: string;
    page: number;
    limit?: number;
  }
): Promise<FieldSearchResponse> {
  const search = new URLSearchParams();
  search.set('page', String(params.page));
  search.set('limit', String(params.limit ?? 20));
  search.set('query', params.query);
  search.set('dataset_id', String(params.datasetId));
  search.set('dataset_type', params.datasetType);

  const url = `${BASE_DATASET_GROUP_URL(
    params.datasetGroupId
  )}/field_search?${search.toString()}`;

  try {
    const response = await fetcher<FieldSearchResponse>(url, {
      method: RequestMethod.GET,
      signal,
    });

    return response;
  } catch (err) {
    new FlyErrorWarning(
      `Error fetching fields for: ${params.datasetId}`,
      {
        label: 'request',
        data: `GET: ${url}`,
      },
      {
        label: 'response',
        data: err,
      }
    ).register('Error in dataset group service');

    throw err;
  }
}

async function checkFieldUniqueness(
  sourceConnectionId: number,
  dataset: string | null,
  table: string,
  columns: string[]
): Promise<CheckFieldUniquenessResponse> {
  const columnsParam = columns.map(c => `columns[]=${c}`).join('&');

  const response = await fetcher<CheckFieldUniquenessResponse>(
    `${BASE_SOURCE_CONNECTION_URL(
      sourceConnectionId
    )}fields/unique?dataset_id=${dataset}&table_id=${table}&${columnsParam}`,
    {
      method: RequestMethod.GET,
    }
  );

  return response;
}

/**
 * Create a dataset group
 */
async function createDatasetGroup(
  payload: Record<string, unknown>
): HttpServiceResponse<HttpServiceError, CreateDatasetGroupResponse> {
  const url = '/api/v2/dataset_groups';
  const response = await httpService.request<CreateDatasetGroupResponse>(url, {
    method: RequestMethod.POST,
    body: payload,
  });

  if (isLeft(response)) {
    const customError = new FlyErrorWarning(
      'Could not create dataset group.',
      {
        label: 'request',
        data: `GET: ${url}`,
      },
      {
        label: 'response',
        data: response.value,
      }
    ).register('Error in dataset group service');
    Object.assign(response, {value: customError});
    return response;
  }

  return response;
}

/**
 * Update a dataset group
 */
async function updateDatasetGroup(params: {
  datasetGroupId: number;
  payload: Record<string, unknown>;
}): HttpServiceResponse<HttpServiceError, UpdateDatasetGroupResponse> {
  const url = `/api/v2/dataset_groups/${params.datasetGroupId}`;
  const response = await httpService.request<UpdateDatasetGroupResponse>(url, {
    method: RequestMethod.PATCH,
    body: params.payload,
  });

  if (isLeft(response)) {
    const customError = new FlyErrorWarning(
      `Could not update ${params.payload.name} dataset group.`,
      {
        label: 'request',
        data: `GET: ${url}`,
      },
      {
        label: 'response',
        data: response.value,
      }
    ).register('Error in dataset group service');
    Object.assign(response, {value: customError});
    return response;
  }

  return response;
}

async function findAllDatasetGroups() {
  const url = '/api/v2/dataset_groups/enriched';
  try {
    const response = await fetcher<FetchAllDatasetGroupsResponse>(url, {
      method: RequestMethod.GET,
    });
    return response.data.map(d => d.attributes);
  } catch (err) {
    console.error(err);
    const userFriendlyError = new Error(
      'Unable to navigate to Audience Studio, please try again later'
    );
    renderErrorNotification(userFriendlyError);
    return left(userFriendlyError);
  }
}

export const datasetGroupService = {
  checkFieldUniqueness,
  createDatasetGroup,
  fieldSearch,
  findAllDatasetGroups,
  updateDatasetGroup,
};
